import {
  getDisposalReportingSchedulePath,
  getDisposalReportingViewInterestPath,
} from '@agency/modules/disposals/pages/disposal-manage/pages/disposal-reporting/DisposalReporting.utils';
import ROUTES from '@agency/routes/routes.constant';
import {
  getLegacyDisposalManageUrl,
  getLegacyRequirementManageUrl,
  getScheduleInvitesUrl,
} from '@agency/utils/redirects';
import { getBaseUrl } from '@core/base-url';
import { t } from '@core/i18n';
import {
  DisposalMatchMessageReceivedData,
  DisposalMatchRequestReceivedData,
  MarketplaceStaleData,
  MatchMessageReceivedData,
  RequirementMatchMessageReceivedData,
  RequirementMatchRequestReceivedData,
  ScheduleInviteReceivedData,
  UserNotificationIndexItem,
  UserNotificationPayloads,
  UserNotificationTarget,
  UserNotificationType,
} from '@shared/apis/notifications';
import { NotificationCategory, NotificationItem } from '@shared/types/notifications';
import { getQueriesAsSearch } from '@shared/utils/common';

export const formatRelativeTime = (iso: string) => {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const diffMinutes = Math.round(diffMs / 60000);

  if (diffMinutes < 1) return t('just_now');
  if (diffMinutes < 60) return t('xcountx_m_ago', { count: diffMinutes });

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return t('xcountx_h_ago', { count: diffHours });

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return t('xcountx_d_ago', { count: diffDays });

  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const formatDate = (iso: string) => {
  if (!iso) {
    return '';
  }

  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface NotificationContent {
  category?: NotificationCategory;
  cta?: NotificationItem['cta'];
  link?: string;
  message: string;
  subtext?: string;
  title: string;
}

// Where the stale and unpublished families can click through to. The disposal targets are
// resolved per request by the backend from the user's disposal saved-view access; the
// requirement views are global shared views, so their encoded ids come straight from config.
export interface NotificationLinkContext {
  requirementsExpiringViewId: number;
  requirementsUnpublishedViewId: number;
  target: UserNotificationTarget | null;
  unpublishedTarget: UserNotificationTarget | null;
}

const DISPOSALS_LIST_PATH = ROUTES.organisation.disposals.listDefault;

// The backend resolves which saved view the user can actually reach, so both links
// are the same list with an optional drawer key appended.
const listUrl = (target: UserNotificationTarget) => `${DISPOSALS_LIST_PATH}${getQueriesAsSearch(target.params)}`;

const listUrlWithDrawer = (target: UserNotificationTarget, disposalId: number) =>
  `${DISPOSALS_LIST_PATH}${getQueriesAsSearch({ ...target.params, [target.mode]: disposalId.toString() })}`;

// The publish to Marketplace slide-outs are still AngularJS, so these are absolute urls out of
// the React app. Both offer to publish again once the listing has been unpublished.
const disposalRepublishUrl = (disposalId: number) =>
  getLegacyDisposalManageUrl(disposalId.toString(), '/marketing/publish-to-society');

const requirementRepublishUrl = (requirementId: number) =>
  getLegacyRequirementManageUrl(requirementId.toString(), '/search-criteria/publish-society');

// The requirements saved-view list is still AngularJS, so this is an absolute url out of
// the React app rather than a router path. A requirement id opens its slide-out over the list.
const requirementsListUrl = (viewId: number, requirementId?: number) =>
  `${getBaseUrl()}#/requirements/list/${viewId}${requirementId ? `/${requirementId}` : ''}`;

// A received match message has no prebuilt link on the payload, so the row lands the user in
// the listing's outbound match area, where the conversation lives.
const disposalMatchesUrl = (disposalId: number, matchId: number) =>
  `${ROUTES.organisation.disposals.manage.root(disposalId.toString())}${
    ROUTES.organisation.disposals.manage.reporting
  }${getQueriesAsSearch({ space: 'outbound', threadId: matchId })}`;

// A single request opens that interest on the schedule; a group lands on the schedule itself.
const disposalScheduleUrl = (disposalId: number, matchId?: number) =>
  matchId
    ? getDisposalReportingViewInterestPath(disposalId.toString(), matchId.toString())
    : getDisposalReportingSchedulePath(disposalId.toString());

// Same shape for the inbound inbox: a single request selects that thread, a group lands on the
// inbox. A thread that has since been moved to the schedule simply is not selected.
const disposalInboundUrl = (disposalId: number, matchId?: number) =>
  `${ROUTES.organisation.disposals.manage.root(disposalId.toString())}${
    ROUTES.organisation.disposals.manage.reporting
  }${getQueriesAsSearch({ space: 'inbound', ...(matchId ? { threadId: matchId.toString() } : {}) })}`;

// With a match id the row opens that match on its messages tab; without one it lands on the
// requirement's match requests tab so the user can pick.
const requirementMatchesUrl = (acquisitionId: number, matchId?: number) =>
  `${ROUTES.organisation.requirements.manage.root(acquisitionId.toString())}${
    ROUTES.organisation.requirements.manage.availability_schedule
  }${getQueriesAsSearch({
    view: 'table',
    table_tab: 'match_requests',
    ...(matchId ? { view_match_id: matchId.toString(), vo_match_tab: 'agent_messages' } : {}),
  })}`;

// The stale listing families keep one row per stage, the way match requests do: a single listing
// is named and opens that listing; a group shows the count and lands on the matching list.
const buildDisposalsMarketplaceStale = (
  data: MarketplaceStaleData,
  context: NotificationLinkContext
): NotificationContent => {
  const { count, listing } = data;

  if (count === 1 && listing) {
    const single: NotificationContent = {
      cta: { label: 'review_disposal' },
      link: context.target ? listUrlWithDrawer(context.target, listing.id) : undefined,
      message: data.auto_unpublish
        ? t(
            'theres_been_no_recent_activity_review_this_disposal_to_keep_it_published_on_marketplace_otherwise_itll_be_unpublished_on_xdatex',
            { date: formatDate(listing.unpublish_date) }
          )
        : t(
            'theres_been_no_recent_activity_review_this_disposal_to_make_sure_the_information_on_marketplace_is_still_current'
          ),
      title: t('marketplace_disposal_for_quote_xnamex_quote_is_due_for_review', { name: listing.name }),
    };

    return single;
  }

  const result: NotificationContent = {
    cta: { label: 'review_disposals' },
    link: context.target ? listUrl(context.target) : undefined,
    message: data.auto_unpublish
      ? t('theyve_had_no_recent_activity_review_them_to_keep_them_published_on_marketplace')
      : t('theyve_had_no_recent_activity_review_them_to_make_sure_the_information_on_marketplace_is_still_current'),
    title: t('xcountx_marketplace_disposals_are_due_for_review', { count }),
  };

  return result;
};

const buildRequirementsMarketplaceStale = (
  data: MarketplaceStaleData,
  context: NotificationLinkContext
): NotificationContent => {
  const { count, listing } = data;

  if (count === 1 && listing) {
    const single: NotificationContent = {
      cta: { label: 'review_requirement' },
      link: context.requirementsExpiringViewId
        ? requirementsListUrl(context.requirementsExpiringViewId, listing.id)
        : undefined,
      message: data.auto_unpublish
        ? t(
            'theres_been_no_recent_activity_review_this_requirement_to_keep_it_published_on_marketplace_otherwise_itll_be_unpublished_on_xdatex',
            { date: formatDate(listing.unpublish_date) }
          )
        : t(
            'theres_been_no_recent_activity_review_this_requirement_to_make_sure_the_information_on_marketplace_is_still_current'
          ),
      title: t('marketplace_requirement_quote_xnamex_quote_is_due_for_review', { name: listing.name }),
    };

    return single;
  }

  const result: NotificationContent = {
    cta: { label: 'review_requirements' },
    link: context.requirementsExpiringViewId ? requirementsListUrl(context.requirementsExpiringViewId) : undefined,
    message: data.auto_unpublish
      ? t('theyve_had_no_recent_activity_review_them_to_keep_them_published_on_marketplace')
      : t('theyve_had_no_recent_activity_review_them_to_make_sure_the_information_on_marketplace_is_still_current'),
    title: t('xcountx_marketplace_requirements_are_due_for_review', { count }),
  };

  return result;
};

const buildDisposalsMarketplaceStaleFinal = (
  data: MarketplaceStaleData,
  context: NotificationLinkContext
): NotificationContent => {
  const { count, listing } = data;

  if (count === 1 && listing) {
    const single: NotificationContent = {
      cta: { label: 'review_disposal' },
      link: context.target ? listUrlWithDrawer(context.target, listing.id) : undefined,
      message: t('review_it_now_to_keep_it_published_on_marketplace'),
      title: t('marketplace_disposal_for_quote_xnamex_quote_will_be_unpublished_in_48_hours', { name: listing.name }),
    };

    return single;
  }

  const result: NotificationContent = {
    cta: { label: 'review_disposals' },
    link: context.target ? listUrl(context.target) : undefined,
    message: t('review_them_now_to_keep_them_published_on_marketplace'),
    title: t('xcountx_marketplace_disposals_will_be_unpublished_in_48_hours', { count }),
  };

  return result;
};

const buildRequirementsMarketplaceStaleFinal = (
  data: MarketplaceStaleData,
  context: NotificationLinkContext
): NotificationContent => {
  const { count, listing } = data;

  if (count === 1 && listing) {
    const single: NotificationContent = {
      cta: { label: 'review_requirement' },
      link: context.requirementsExpiringViewId
        ? requirementsListUrl(context.requirementsExpiringViewId, listing.id)
        : undefined,
      message: t('review_it_now_to_keep_it_published_on_marketplace'),
      title: t('marketplace_requirement_quote_xnamex_quote_will_be_unpublished_in_48_hours', { name: listing.name }),
    };

    return single;
  }

  const result: NotificationContent = {
    cta: { label: 'review_requirements' },
    link: context.requirementsExpiringViewId ? requirementsListUrl(context.requirementsExpiringViewId) : undefined,
    message: t('review_them_now_to_keep_them_published_on_marketplace'),
    title: t('xcountx_marketplace_requirements_will_be_unpublished_in_48_hours', { count }),
  };

  return result;
};

const buildDisposalsMarketplaceUnpublished = (
  data: MarketplaceStaleData,
  context: NotificationLinkContext
): NotificationContent => {
  const { count, listing } = data;

  if (count === 1 && listing) {
    const single: NotificationContent = {
      cta: { label: 'view_disposal' },
      link: disposalRepublishUrl(listing.id),
      message: t('it_had_no_recent_activity_you_can_update_it_and_publish_it_again_at_any_time'),
      title: t('marketplace_disposal_for_quote_xnamex_quote_has_been_unpublished', { name: listing.name }),
    };

    return single;
  }

  const result: NotificationContent = {
    cta: { label: 'view_unpublished_disposals' },
    link: context.unpublishedTarget ? listUrl(context.unpublishedTarget) : undefined,
    message: t('they_had_no_recent_activity_you_can_update_them_and_publish_them_again_at_any_time'),
    title: t('xcountx_marketplace_disposals_have_been_unpublished', { count }),
  };

  return result;
};

const buildRequirementsMarketplaceUnpublished = (
  data: MarketplaceStaleData,
  context: NotificationLinkContext
): NotificationContent => {
  const { count, listing } = data;

  if (count === 1 && listing) {
    const single: NotificationContent = {
      cta: { label: 'view_requirement' },
      link: requirementRepublishUrl(listing.id),
      message: t('it_had_no_recent_activity_you_can_update_it_and_publish_it_again_at_any_time'),
      title: t('marketplace_requirement_quote_xnamex_quote_has_been_unpublished', { name: listing.name }),
    };

    return single;
  }

  const result: NotificationContent = {
    cta: { label: 'view_unpublished_requirements' },
    link: context.requirementsUnpublishedViewId
      ? requirementsListUrl(context.requirementsUnpublishedViewId)
      : undefined,
    message: t('they_had_no_recent_activity_you_can_update_them_and_publish_them_again_at_any_time'),
    title: t('xcountx_marketplace_requirements_have_been_unpublished', { count }),
  };

  return result;
};

// One row per requirement: the backend folds further match requests into the live row and bumps
// `count`, so the same row reads as a single request or a group. A single request opens that match;
// a group lands on the requirement's match requests list so the user can pick.
const buildRequirementMatchRequestReceived = (data: RequirementMatchRequestReceivedData): NotificationContent => {
  const { count = 1, requirement_title: name } = data;
  const grouped = count > 1;

  const result: NotificationContent = {
    cta: { label: grouped ? 'view_matches' : 'view_match' },
    link: requirementMatchesUrl(data.requirement_id, grouped ? undefined : data.match_ids[0]),
    message: grouped
      ? t('agents_matched_their_disposals_to_your_requirement')
      : t('an_agent_matched_a_disposal_to_your_requirement'),
    title: grouped
      ? t('xcountx_new_match_requests_for_quote_xnamex_quote', { count, name })
      : t('new_match_request_for_quote_xnamex_quote', { name }),
  };

  return result;
};

// The disposal-side twin of buildRequirementMatchRequestReceived: one row per disposal, folded
// and counted by the backend the same way. Unlike the requirement side, a disposal may file
// requests either in its inbound inbox or straight onto the schedule, so the backend records
// which (`target`) at write time.
const buildDisposalMatchRequestReceived = (data: DisposalMatchRequestReceivedData): NotificationContent => {
  const { count = 1, property_address: name } = data;
  const grouped = count > 1;
  const toUrl = data.target === 'inbound' ? disposalInboundUrl : disposalScheduleUrl;

  const result: NotificationContent = {
    cta: { label: grouped ? 'view_matches' : 'view_match' },
    link: toUrl(data.disposal_id, grouped ? undefined : data.match_ids[0]),
    message: grouped
      ? t('agents_matched_their_requirements_to_your_disposal')
      : t('an_agent_matched_a_requirement_to_your_disposal'),
    title: grouped
      ? t('xcountx_new_match_requests_for_quote_xnamex_quote', { count, name })
      : t('new_match_request_for_quote_xnamex_quote', { name }),
  };

  return result;
};

// The message body is deliberately never rendered here. A notification panel is a low-trust
// surface — it renders wherever the user happens to be, including on a shared screen — so it
// says who sent it and which listing it is about, and sends the user to the thread to read it.
// Both sides read the same; only the listing the link lands on differs.
const buildMatchMessageReceived = (data: MatchMessageReceivedData, link: string): NotificationContent => {
  const { count = 1, sender_name: senderName } = data;
  const senderForename = data.sender_forename || senderName;
  // The count covers the whole thread, but the sender is whoever wrote last, so a group can be
  // several people from the same side.
  const grouped = count > 1;

  const message = grouped
    ? data.property_address
      ? t('xnamex_sent_you_xcountx_messages_about_xlistingx', {
          count,
          listing: data.property_address,
          name: senderForename,
        })
      : t('xnamex_sent_you_xcountx_messages', { count, name: senderForename })
    : data.property_address
    ? t('xnamex_sent_you_a_message_about_xlistingx', { listing: data.property_address, name: senderForename })
    : t('xnamex_sent_you_a_message', { name: senderForename });

  const titleKey = grouped
    ? 'messages_from_xnamex_bracket_xorganisationx_bracket'
    : 'message_from_xnamex_bracket_xorganisationx_bracket';

  const result: NotificationContent = {
    category: 'message',
    cta: { label: 'view_message' },
    link,
    message,
    title: t(titleKey, { name: senderName, organisation: data.sender_organisation_name }),
  };

  return result;
};

const buildDisposalMatchMessageReceived = (data: DisposalMatchMessageReceivedData): NotificationContent =>
  buildMatchMessageReceived(data, disposalMatchesUrl(data.disposal_id, data.match_id));

const buildRequirementMatchMessageReceived = (data: RequirementMatchMessageReceivedData): NotificationContent =>
  buildMatchMessageReceived(data, requirementMatchesUrl(data.acquisition_id, data.match_id));

// The notification never carries the invite's current outcome (accepting/declining does not
// rewrite it — that is the whole point), so the row itself shows no outcome of its own. The
// schedule inbox modal it links to resolves the real state and renders accordingly.
const buildScheduleInviteReceived = (data: ScheduleInviteReceivedData): NotificationContent => {
  // The schedule invites show endpoint (and its `received_details` deep link) key on the
  // InvitationGroup id, not the Invitation id — the same distinction PublicInvitationController
  // and InvitationSent already rely on for their own links into this page.
  // The schedule invites page resolves an invite's real state itself (pending/accepted/
  // declined/revoked) regardless of the tab it lands on, so `status` here is only a sensible
  // starting point for the table behind the modal — the page corrects it once the invite loads.
  const result: NotificationContent = {
    category: 'invite',
    cta: { label: 'review_invite' },
    link: getScheduleInvitesUrl({ received_details: data.invitation_group_id.toString(), tab: data.status }),
    message: t('xnamex_bracket_xorganisationx_bracket_has_invited_you_to_join_a_shared_interest_schedule', {
      name: data.sender_name,
      organisation: data.sender_organisation_name,
    }),
    subtext: data.property_address || undefined,
    title: t('xnamex_wants_to_collaborate', { name: data.sender_name }),
  };

  return result;
};

type ContentBuilder<TType extends UserNotificationType> = (
  data: UserNotificationPayloads[TType],
  context: NotificationLinkContext
) => NotificationContent;

const CONTENT_BUILDERS: { [TType in UserNotificationType]?: ContentBuilder<TType> } = {
  disposal_match_message_received: buildDisposalMatchMessageReceived,
  disposal_match_request_received: buildDisposalMatchRequestReceived,
  disposals_marketplace_stale: buildDisposalsMarketplaceStale,
  disposals_marketplace_stale_final: buildDisposalsMarketplaceStaleFinal,
  disposals_marketplace_unpublished: buildDisposalsMarketplaceUnpublished,
  requirement_match_message_received: buildRequirementMatchMessageReceived,
  requirement_match_request_received: buildRequirementMatchRequestReceived,
  requirements_marketplace_stale: buildRequirementsMarketplaceStale,
  requirements_marketplace_stale_final: buildRequirementsMarketplaceStaleFinal,
  requirements_marketplace_unpublished: buildRequirementsMarketplaceUnpublished,
  schedule_invite_received: buildScheduleInviteReceived,
};

// Read-only surfaces (e.g. history) never render a link or cta, so they have no real
// link context to resolve - this is what "no context" means to every builder above.
const NO_LINK_CONTEXT: NotificationLinkContext = {
  requirementsExpiringViewId: 0,
  requirementsUnpublishedViewId: 0,
  target: null,
  unpublishedTarget: null,
};

// Unknown/future notification types (the backend enum defines more than v1 emits)
// are skipped here and filtered out by the hook, rather than crashing the panel.
export const adaptNotification = (
  api: UserNotificationIndexItem,
  context: NotificationLinkContext = NO_LINK_CONTEXT
): NotificationItem | null => {
  // TypeScript cannot correlate `api.type` with the builder looked up by it across the union
  // (microsoft/TypeScript#30581), so this one seam widens to a builder of any payload.
  const builder = CONTENT_BUILDERS[api.type] as ContentBuilder<UserNotificationType> | undefined;

  if (!builder) {
    return null;
  }

  const content = builder(api.data, context);

  const result: NotificationItem = {
    category: content.category ?? 'general',
    createdAt: api.notified_at,
    cta: content.cta,
    id: api.id.toString(),
    link: content.link,
    message: content.message,
    notificationType: api.type,
    subtext: content.subtext,
    title: content.title,
    type: 'link',
  };

  return result;
};
